import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import Header from '@/components/layout/Header';
import MobileNav from '@/components/layout/MobileNav';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Loader2, AlertCircle, Sparkles, Home, Send, UserPlus,
  Camera, Heart, Users, TrendingUp, AlertTriangle, ArrowDown,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const EVENTS = [
  { key: 'family_created',       label: 'Families created',     icon: Home,     color: '#0d9488' },
  { key: 'invite_sent',          label: 'Invites sent',         icon: Send,     color: '#0891b2' },
  { key: 'invite_accepted',      label: 'Invites accepted',     icon: UserPlus, color: '#10b981' },
  { key: 'first_photo_uploaded', label: 'First photo uploads',  icon: Camera,   color: '#f59e0b' },
  { key: 'first_reaction',       label: 'First reactions',      icon: Heart,    color: '#ec4899' },
];

function StatCard({ icon: Icon, label, value, hint, color, testid }) {
  return (
    <Card className="p-5 rounded-2xl border-stone-200 bg-white" data-testid={testid}>
      <div className="flex items-start gap-3">
        <div
          className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
          style={{ backgroundColor: `${color}1A` }}
        >
          <Icon className="w-5 h-5" style={{ color }} />
        </div>
        <div className="min-w-0">
          <div className="text-xs text-stone-500 font-medium uppercase tracking-wide">{label}</div>
          <div className="text-2xl font-semibold text-stone-900 mt-0.5">{value}</div>
          {hint && <div className="text-xs text-stone-500 mt-1">{hint}</div>}
        </div>
      </div>
    </Card>
  );
}

// Lightweight inline SVG sparkline — no chart library, no deps.
function SparkChart({ series, eventKey, color }) {
  const points = series.map((d) => d[eventKey] || 0);
  const max = Math.max(1, ...points);
  const W = 280;
  const H = 60;
  const stepX = points.length > 1 ? W / (points.length - 1) : W;

  const path = points
    .map((v, i) => {
      const x = i * stepX;
      const y = H - (v / max) * (H - 6) - 3;
      return `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');

  const areaPath = `${path} L${W},${H} L0,${H} Z`;

  return (
    <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} className="w-full h-14">
      <path d={areaPath} fill={color} fillOpacity="0.10" />
      <path d={path} fill="none" stroke={color} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export default function AdminAnalyticsPage() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [fetching, setFetching] = useState(true);
  const [error, setError] = useState(null);

  // Owner gating
  useEffect(() => {
    if (!loading && (!user || !user.is_owner)) {
      navigate('/');
    }
  }, [loading, user, navigate]);

  const load = async (windowDays) => {
    setFetching(true);
    setError(null);
    try {
      const { data } = await axios.get(
        `${API}/admin/analytics?days=${windowDays}`,
        { withCredentials: true },
      );
      setData(data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not load analytics');
    } finally {
      setFetching(false);
    }
  };

  useEffect(() => { load(days); /* eslint-disable-next-line */ }, [days]);

  const wins = data?.totals || {};
  const allTime = data?.all_time_totals || {};
  const series = data?.daily_series || [];

  const conversion = useMemo(() => {
    const sent = allTime.invite_sent || 0;
    const accepted = allTime.invite_accepted || 0;
    if (!sent) return null;
    return Math.round((accepted / sent) * 100);
  }, [allTime]);

  return (
    <div className="min-h-screen bg-[#FDFBF7] pb-20 md:pb-8" data-testid="admin-analytics-page">
      <Header />

      <main className="max-w-6xl mx-auto px-4 py-6">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
          <div>
            <div className="flex items-center gap-2 text-xs font-medium text-teal-700 bg-teal-50 border border-teal-200 rounded-full px-2.5 py-1 w-fit mb-2">
              <Sparkles className="w-3 h-3" /> Owner-only
            </div>
            <h1 className="text-2xl font-bold text-stone-900">Product analytics</h1>
            <p className="text-sm text-stone-500 mt-1">
              Funnel, activation and retention for the last {days} days.
            </p>
          </div>
          <div className="flex gap-2">
            {[7, 30, 90].map((d) => (
              <Button
                key={d}
                variant={days === d ? 'default' : 'outline'}
                onClick={() => setDays(d)}
                className={
                  days === d
                    ? 'bg-teal-600 hover:bg-teal-700 text-white rounded-full'
                    : 'rounded-full'
                }
                data-testid={`window-${d}`}
              >
                {d}d
              </Button>
            ))}
          </div>
        </div>

        {fetching ? (
          <div className="text-center py-16">
            <Loader2 className="w-8 h-8 text-teal-600 animate-spin mx-auto mb-2" />
            <p className="text-stone-500 text-sm">Loading analytics…</p>
          </div>
        ) : error ? (
          <Card className="p-6 rounded-2xl border-red-200 bg-red-50">
            <div className="flex items-center gap-3 text-red-700">
              <AlertCircle className="w-5 h-5" />
              <span className="font-medium">{error}</span>
            </div>
          </Card>
        ) : (
          <>
            {/* Top row — engagement summary */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <StatCard
                icon={Users}
                color="#0d9488"
                label="Weekly active families"
                value={data.weekly_active_families ?? 0}
                hint="Distinct families with any event in last 7d"
                testid="stat-wkly-active"
              />
              <StatCard
                icon={Home}
                color="#0891b2"
                label="Families (all time)"
                value={allTime.family_created ?? 0}
                hint={`+${wins.family_created ?? 0} in ${days}d`}
                testid="stat-families"
              />
              <StatCard
                icon={Send}
                color="#9333ea"
                label="Invites sent (all time)"
                value={allTime.invite_sent ?? 0}
                hint={`+${wins.invite_sent ?? 0} in ${days}d`}
                testid="stat-invites"
              />
              <StatCard
                icon={TrendingUp}
                color="#10b981"
                label="Invite → join conversion"
                value={conversion == null ? '—' : `${conversion}%`}
                hint={`${allTime.invite_accepted ?? 0} accepted of ${allTime.invite_sent ?? 0}`}
                testid="stat-conversion"
              />
            </div>

            {/* Funnel — the core Kinsy loop */}
            <h2 className="text-lg font-semibold text-stone-900 mb-1">The core loop</h2>
            <p className="text-sm text-stone-500 mb-4">
              <span className="font-medium text-stone-700">Family created → Invite sent → Invite accepted → First photo → First reaction</span>.
              This is the activation funnel beta / investor reviews care about.
            </p>
            {data.funnel?.steps?.length > 0 && (
              <Card className="p-5 rounded-2xl border-stone-200 bg-white mb-6" data-testid="funnel-card">
                <div className="space-y-3">
                  {(() => {
                    const steps = data.funnel.steps;
                    const startCount = steps[0]?.count || 0;
                    return steps.map((s, i) => {
                      const eventConfig = EVENTS.find((e) => e.key === s.key) || {};
                      const Icon = eventConfig.icon || Users;
                      const color = eventConfig.color || '#0d9488';
                      const widthPct = startCount > 0
                        ? Math.max(2, Math.round((s.count / startCount) * 100))
                        : 2;
                      return (
                        <div key={s.key} data-testid={`funnel-step-${s.key}`}>
                          <div className="flex items-center gap-3 mb-1">
                            <div
                              className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
                              style={{ backgroundColor: `${color}1A` }}
                            >
                              <Icon className="w-4 h-4" style={{ color }} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-baseline justify-between gap-2 flex-wrap">
                                <span className="font-semibold text-stone-900">{s.label}</span>
                                <div className="text-xs text-stone-500 flex items-center gap-3">
                                  {i > 0 && s.conversion_from_prev != null && (
                                    <span data-testid={`from-prev-${s.key}`}>
                                      <span className="text-stone-400">from previous</span>{' '}
                                      <span className="font-medium text-stone-700">{s.conversion_from_prev}%</span>
                                    </span>
                                  )}
                                  {i > 0 && s.conversion_from_start != null && (
                                    <span data-testid={`from-start-${s.key}`}>
                                      <span className="text-stone-400">overall</span>{' '}
                                      <span className="font-medium text-stone-700">{s.conversion_from_start}%</span>
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="ml-11 relative h-9 rounded-lg bg-stone-50 overflow-hidden">
                            <div
                              className="h-full rounded-lg flex items-center px-3 text-white text-sm font-semibold transition-all"
                              style={{ width: `${widthPct}%`, backgroundColor: color, minWidth: '2.5rem' }}
                            >
                              {s.count}
                            </div>
                          </div>
                        </div>
                      );
                    });
                  })()}
                </div>

                {/* Top drop-off callout */}
                {data.funnel.top_dropoff && (
                  <div
                    className="mt-5 rounded-xl border border-amber-200 bg-amber-50 p-4 flex items-start gap-3"
                    data-testid="top-dropoff"
                  >
                    <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                    <div className="text-sm">
                      <div className="font-semibold text-amber-900">
                        Biggest drop-off: {data.funnel.top_dropoff.from_label}
                        <ArrowDown className="inline w-3.5 h-3.5 mx-1 -mt-0.5 text-amber-700" />
                        {data.funnel.top_dropoff.to_label}
                      </div>
                      <div className="text-amber-800">
                        <span className="font-semibold">{data.funnel.top_dropoff.dropoff_pct}%</span> drop —{' '}
                        {data.funnel.top_dropoff.dropped_users} families never reached the next step
                        in the last {days} days.
                      </div>
                    </div>
                  </div>
                )}
              </Card>
            )}

            {/* Extended-family / grandparent participation */}
            {data.extended_family_participation && (
              <Card
                className="p-5 rounded-2xl border-stone-200 bg-white mb-8"
                data-testid="ext-participation-card"
              >
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl bg-pink-50 flex items-center justify-center shrink-0">
                    <Heart className="w-5 h-5 text-pink-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline justify-between gap-2 flex-wrap">
                      <span className="font-semibold text-stone-900">Extended family participation</span>
                      <span className="text-xs text-stone-400">last {days}d</span>
                    </div>
                    {data.extended_family_participation.total_extended_members === 0 ? (
                      <p className="text-sm text-stone-500 mt-1">
                        No view-only members invited yet. Once parents invite
                        grandparents / extended family, their engagement rate will
                        show up here.
                      </p>
                    ) : (
                      <>
                        <div className="flex items-baseline gap-3 mt-1">
                          <span className="text-2xl font-semibold text-stone-900">
                            {data.extended_family_participation.participation_pct ?? '—'}%
                          </span>
                          <span className="text-sm text-stone-500">
                            {data.extended_family_participation.active_in_window} of{' '}
                            {data.extended_family_participation.total_extended_members} active
                          </span>
                        </div>
                        <p className="text-xs text-stone-500 mt-2">
                          Of all view-only family members (typically grandparents
                          and relatives), the percentage who reacted, posted or
                          otherwise engaged in the window. Investors will ask
                          about this number directly.
                        </p>
                      </>
                    )}
                  </div>
                </div>
              </Card>
            )}

            {/* Per-event trend lines */}
            <h2 className="text-lg font-semibold text-stone-900 mb-3">Trends</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {EVENTS.map((ev) => (
                <Card key={ev.key} className="p-5 rounded-2xl border-stone-200 bg-white" data-testid={`trend-${ev.key}`}>
                  <div className="flex items-start gap-3 mb-2">
                    <div
                      className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
                      style={{ backgroundColor: `${ev.color}1A` }}
                    >
                      <ev.icon className="w-4 h-4" style={{ color: ev.color }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline gap-2">
                        <span className="font-semibold text-stone-900">{ev.label}</span>
                        <span className="text-xs text-stone-400">last {days}d</span>
                      </div>
                      <div className="flex items-baseline gap-2 mt-0.5">
                        <span className="text-xl font-semibold text-stone-900">{wins[ev.key] ?? 0}</span>
                        <span className="text-xs text-stone-500">all-time {allTime[ev.key] ?? 0}</span>
                      </div>
                    </div>
                  </div>
                  <SparkChart series={series} eventKey={ev.key} color={ev.color} />
                </Card>
              ))}
            </div>

            <p className="text-xs text-stone-400 mt-8">
              Events stored in <code>db.analytics_events</code>. First-time events
              (first_photo_uploaded, first_reaction) are idempotent — they fire at
              most once per family/user.
            </p>
          </>
        )}
      </main>

      <MobileNav />
    </div>
  );
}
